import { useState } from "react";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const PERIODS = [
  { key: "month", label: "Mese corrente", vs: "vs mese prec." },
  { key: "prev_month", label: "Mese precedente", vs: "vs mese prima" },
  { key: "ytd", label: "Da inizio anno", vs: "vs stesso periodo anno prec." },
];

const MONTH_RE = /^20\d{2}-(0[1-9]|1[0-2])$/;
const monthName = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" });
const monthText = (key) => { const s = monthName.format(new Date(`${key}-01T12:00:00`)); return s[0].toUpperCase() + s.slice(1); };

// mesi dell'anno corrente e del precedente, dal più recente
const monthOptions = () => {
  const now = new Date();
  const out = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let m = y === now.getFullYear() ? now.getMonth() + 1 : 12; m >= 1; m--) out.push(`${y}-${String(m).padStart(2, "0")}`);
  }
  return out;
};

export const usePeriod = (storageKey) => {
  const saved = localStorage.getItem(storageKey);
  const [period, setPeriod] = useState(PERIODS.some((p) => p.key === saved) || MONTH_RE.test(saved || "") ? saved : "month");
  const update = (p) => { localStorage.setItem(storageKey, p); setPeriod(p); };
  return [period, update];
};

export const periodLabel = (key) => (MONTH_RE.test(key) ? monthText(key) : PERIODS.find((p) => p.key === key)?.label);
const vsLabel = (key) => (MONTH_RE.test(key) ? "vs mese prima" : PERIODS.find((p) => p.key === key)?.vs);

const MonthPicker = ({ value, onChange, testId }) => {
  const picked = MONTH_RE.test(value);
  return (
    <Select value={picked ? value : ""} onValueChange={onChange}>
      <SelectTrigger data-testid={`${testId}-month`} className={cn("h-8 w-44 rounded-full text-xs", picked && "border-ink bg-ink text-white")}>
        <SelectValue placeholder="Scegli mese…" />
      </SelectTrigger>
      <SelectContent>
        {monthOptions().map((m) => <SelectItem key={m} value={m} data-testid={`${testId}-month-${m}`}>{monthText(m)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const PeriodFilter = ({ value, onChange, testId = "period-filter" }) => (
  <div className="flex flex-wrap items-center gap-2">
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" role="group" data-testid={testId}>
    {PERIODS.map((p) => (
      <button key={p.key} type="button" onClick={() => onChange(p.key)} data-testid={`${testId}-${p.key}`} aria-pressed={value === p.key}
        className={cn("rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150",
          value === p.key ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100")}>
        {p.label}
      </button>
    ))}
  </div>
  <MonthPicker value={value} onChange={onChange} testId={testId} />
  </div>
);

// points=true → differenza in punti percentuali (es. tasso di successo)
export const Delta = ({ cur, prev, period, points = false, testId }) => {
  if (cur === null || cur === undefined || prev === null || prev === undefined) return null;
  const vs = vsLabel(period);
  let text, tone;
  if (points) {
    const d = cur - prev;
    text = `${d > 0 ? "+" : ""}${d} pt`;
    tone = d > 0 ? "up" : d < 0 ? "down" : "flat";
  } else if (!prev) {
    text = cur ? "nuovo" : "=";
    tone = cur ? "up" : "flat";
  } else {
    const d = Math.round(((cur - prev) / Math.abs(prev)) * 100);
    text = `${d > 0 ? "+" : ""}${d}%`;
    tone = d > 0 ? "up" : d < 0 ? "down" : "flat";
  }
  return (
    <span className="text-[11px] text-slate-400" data-testid={testId}>
      <span className={cn("font-semibold", tone === "up" ? "text-emerald-600" : tone === "down" ? "text-red-600" : "text-slate-500")}>{text}</span> {vs}
    </span>
  );
};
